import { useEffect, useMemo, useState } from "react";
import Badge from "@/components/base/Badge";
import { useSession } from "@/auth/session";
import {
  archiveOperation,
  assignOperationVehicle,
  assignOperationWorkers,
  completeOperation,
  createOperation,
  createStockIssueVoucher,
  listContracts,
  listCustomers,
  listInventoryItems,
  listOperarios,
  listOperations,
  listOperationTypes,
  listVehicles,
  listWarehouses,
  reviewOperation,
  startOperation,
  unarchiveOperation,
  type ContractListItem,
  type CustomerListItem,
  type InventoryItemOption,
  type OperarioItem,
  type OperationItem,
  type OperationTypeItem,
  type VehicleItem,
  type WarehouseItem,
} from "@/lib/api";

type ViewMode = "kanban" | "lista" | "historial";
type ColumnId = "pendiente" | "en_proceso" | "completada";

type UiOperation = {
  id: string;
  sequenceNumber: number;
  displayCode: string;
  status: string;
  column: ColumnId;
  customerLabel: string;
  contractLabel: string;
  operationLabel: string;
  operationCategory: string;
  priorityLabel: "alta" | "media" | "baja";
  priorityVariant: "error" | "warning" | "success";
  summary: string;
  diagnosis: string;
  resolution: string;
  scheduledAt: string;
  startedAt: string;
  completedAt: string;
  requestedAt: string;
  technicianLabel: string;
  vehicles: OperationItem["vehicles"];
  materials: OperationItem["materials"];
};

type NewOperationForm = {
  contractId: string;
  operationTypeId: string;
  priority: string;
  scheduledAt: string;
  requestSummary: string;
};

const columnConfig: Array<{ id: ColumnId; label: string; color: string }> = [
  { id: "pendiente", label: "Pendiente", color: "bg-amber-500" },
  { id: "en_proceso", label: "En Proceso", color: "bg-blue-500" },
  { id: "completada", label: "Completada", color: "bg-emerald-500" },
];

const categoryConfig: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  reparacion: { label: "Reparacion", icon: "ri-tools-line", color: "text-orange-600", bg: "bg-orange-100" },
  instalacion: { label: "Instalacion", icon: "ri-add-circle-line", color: "text-blue-600", bg: "bg-blue-100" },
  lectura: { label: "Lectura", icon: "ri-dashboard-line", color: "text-violet-600", bg: "bg-violet-100" },
  corte: { label: "Corte", icon: "ri-scissors-cut-line", color: "text-red-600", bg: "bg-red-100" },
};

const emptyForm: NewOperationForm = {
  contractId: "",
  operationTypeId: "",
  priority: "normal",
  scheduledAt: "",
  requestSummary: "",
};

export default function OperacionesPage() {
  const { session } = useSession();

  const [operations, setOperations] = useState<OperationItem[]>([]);
  const [archivedOperations, setArchivedOperations] = useState<OperationItem[]>([]);
  const [operationTypes, setOperationTypes] = useState<OperationTypeItem[]>([]);
  const [vehicles, setVehicles] = useState<VehicleItem[]>([]);
  const [operarios, setOperarios] = useState<OperarioItem[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItemOption[]>([]);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [historialSearch, setHistorialSearch] = useState("");
  const [loadingArchived, setLoadingArchived] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState<ViewMode>("kanban");
  const [showNuevaOT, setShowNuevaOT] = useState(false);
  const [selectedOperationId, setSelectedOperationId] = useState<string | null>(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState<CustomerListItem[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerListItem | null>(null);
  const [customerContracts, setCustomerContracts] = useState<ContractListItem[]>([]);
  const [loadingCustomerContracts, setLoadingCustomerContracts] = useState(false);

  const [newForm, setNewForm] = useState<NewOperationForm>(emptyForm);
  const [actionNotes, setActionNotes] = useState("");
  const [actionResolution, setActionResolution] = useState("");

  const [workerOperarioId, setWorkerOperarioId] = useState("");
  const [workerRole, setWorkerRole] = useState("tecnico");
  const [vehicleId, setVehicleId] = useState("");

  const [warehouseId, setWarehouseId] = useState("");
  const [inventoryItemId, setInventoryItemId] = useState("");
  const [materialQty, setMaterialQty] = useState(1);
  const [materialNotes, setMaterialNotes] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadData(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");
      try {
        const [
          operationsData,
          typesData,
          vehiclesData,
          operariosData,
          warehousesData,
          inventoryData,
        ] = await Promise.all([
          listOperations(session),
          listOperationTypes(session),
          listVehicles(session),
          listOperarios(session),
          listWarehouses(session),
          listInventoryItems(session, true),
        ]);

        if (cancelled) return;

        setOperations(operationsData);
        setOperationTypes(typesData);
        setVehicles(vehiclesData);
        setOperarios(operariosData);
        setWarehouses(warehousesData);
        setInventoryItems(inventoryData);
        setWarehouseId(warehousesData[0]?.id ?? "");
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar operaciones");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadData();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const operationTypeById = useMemo(() => {
    const map = new Map<string, OperationTypeItem>();
    operationTypes.forEach((type) => map.set(type.id, type));
    return map;
  }, [operationTypes]);

  const uiOperations = useMemo<UiOperation[]>(() => {
    return operations.map((operation) => {
      const type = operationTypeById.get(operation.operationTypeId);
      const category = normalizeCategory(operation.operationTypeCategory || type?.category || "reparacion");

      const customerLabel =
        operation.customerName ||

        `${operation.contractNumber || "Contrato"}${operation.contractAddress ? ` · ${operation.contractAddress}` : ""}`;
      const assigned = operation.assignments
        .map((a) => a.operarioId ?? a.userId ?? "")
        .filter(Boolean)
        .join(", ");

      return {
        id: operation.id,
        sequenceNumber: operation.sequenceNumber,
        displayCode: formatOperationCode(operation.sequenceNumber),
        status: operation.status,
        column: statusToColumn(operation.status),
        customerLabel,
        contractLabel: `${operation.contractNumber || "Sin contrato"}${operation.contractAddress ? ` · ${operation.contractAddress}` : ""}`,
        operationLabel: operation.operationTypeName || type?.name || "Operacion",
        operationCategory: category,
        priorityLabel: mapPriorityLabel(operation.priority),
        priorityVariant: mapPriorityVariant(operation.priority),
        summary: operation.requestSummary,
        diagnosis: operation.diagnosis,
        resolution: operation.resolution,
        scheduledAt: toDateLabel(operation.scheduledAt),
        startedAt: toDateLabel(operation.startedAt),
        completedAt: toDateLabel(operation.completedAt),
        requestedAt: toDateLabel(operation.requestedAt),
        technicianLabel: assigned || "Sin asignar",
        vehicles: operation.vehicles,
        materials: operation.materials,
      };
    });
  }, [operationTypeById, operations]);

  const selectedOperation = useMemo(
    () => uiOperations.find((operation) => operation.id === selectedOperationId) ?? null,
    [selectedOperationId, uiOperations],
  );

  const stats = useMemo(() => {
    return {
      pendiente: uiOperations.filter((operation) => operation.column === "pendiente").length,
      enProceso: uiOperations.filter((operation) => operation.column === "en_proceso").length,
      completada: uiOperations.filter((operation) => operation.column === "completada").length,
    };
  }, [uiOperations]);

  useEffect(() => {
    let cancelled = false;
    if (!session || !showNuevaOT) return;
    if (selectedCustomer) return;

    const term = customerSearchTerm.trim();
    if (term.length < 2) {
      setCustomerSearchResults([]);
      setSearchingCustomers(false);
      return;
    }

    const timer = window.setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const results = await listCustomers(session, { q: term, limit: 25 });
        if (!cancelled) setCustomerSearchResults(results);
      } catch {
        if (!cancelled) setCustomerSearchResults([]);
      } finally {
        if (!cancelled) setSearchingCustomers(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [customerSearchTerm, selectedCustomer, session, showNuevaOT]);

  async function loadContractsForCustomer(customerId: string): Promise<void> {
    if (!session) return;
    setLoadingCustomerContracts(true);
    try {
      const items = await listContracts(session, {
        customerId,
        status: "active",
        limit: 100,
      });
      setCustomerContracts(items);
    } catch {
      setCustomerContracts([]);
    } finally {
      setLoadingCustomerContracts(false);
    }
  }

  async function handleSelectCustomer(selected: CustomerListItem): Promise<void> {
    setSelectedCustomer(selected);
    setCustomerSearchTerm(`${selected.nombre} · ${selected.rut}`);
    setCustomerSearchResults([]);
    setSearchingCustomers(false);
    setNewForm((previous) => ({ ...previous, contractId: "" }));
    await loadContractsForCustomer(selected.id);
  }

  function handleResetSelectedCustomer(): void {
    setSelectedCustomer(null);
    setCustomerSearchTerm("");
    setCustomerSearchResults([]);
    setCustomerContracts([]);
    setNewForm((previous) => ({ ...previous, contractId: "" }));
  }

  function openNewOperationModal(): void {
    setError("");
    setNewForm(emptyForm);
    setCustomerSearchTerm("");
    setCustomerSearchResults([]);
    setSelectedCustomer(null);
    setCustomerContracts([]);
    setShowNuevaOT(true);
  }

  function replaceOperationInState(updated: OperationItem): void {
    setOperations((previous) => previous.map((operation) => (operation.id === updated.id ? updated : operation)));
  }

  function openOperationPrintWindow(): Window | null {
    const printWindow = window.open("", "_blank", "width=960,height=1200");
    if (!printWindow) {
      setError("No se pudo abrir la hoja imprimible de la OT.");
      return null;
    }

    printWindow.document.open();
    printWindow.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8" /><title>Preparando OT...</title></head><body style="font-family: Arial, sans-serif; padding: 24px; color: #111827;"><p>Preparando hoja imprimible de la OT...</p></body></html>`);
    printWindow.document.close();
    return printWindow;
  }

  function openOperationPrintSheet(
    operation: OperationItem,
    printWindow = openOperationPrintWindow(),
  ): void {
    if (!printWindow) {
      return;
    }

    const assignedWorkers = operation.assignments
      .map((assignment) => {
        const operario = operarios.find((item) => item.id === assignment.operarioId);
        const label = operario?.name || assignment.userId || assignment.operarioId || "Sin nombre";
        return `${label} - ${assignment.roleInOperation}`;
      })
      .filter(Boolean);
    const assignedVehicles = operation.vehicles
      .map((vehicle) => [vehicle.code, vehicle.name, vehicle.plate].filter(Boolean).join(" - "))
      .filter(Boolean);
    const materialRows = Array.from({ length: 6 }, (_, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
      </tr>
    `).join("");

    const html = `<!doctype html>
<html lang="es">
  <head>
    <meta charset="utf-8" />
    <title>Resumen ${escapeHtml(operation.displayCode)}</title>
    <style>
      @page { size: A4; margin: 14mm; }
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; color: #111827; margin: 0; font-size: 12px; background: #ffffff; }
      h1, h2, h3, p { margin: 0; }
      .sheet { display: flex; flex-direction: column; gap: 14px; }
      .header { border: 1.5px solid #1f2937; border-radius: 10px; overflow: hidden; }
      .header-top { display: grid; grid-template-columns: 1.4fr 1fr; }
      .brand { padding: 14px 16px; border-right: 1px solid #1f2937; }
      .brand-kicker { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #4b5563; }
      .brand-title { margin-top: 4px; font-size: 22px; font-weight: 700; }
      .brand-subtitle { margin-top: 4px; color: #4b5563; font-size: 12px; }
      .docbox { padding: 14px 16px; background: #f8fafc; }
      .docbox-title { font-size: 11px; text-transform: uppercase; color: #6b7280; }
      .docbox-number { margin-top: 4px; font-size: 22px; font-weight: 700; }
      .docbox-meta { margin-top: 8px; display: grid; gap: 4px; font-size: 11px; color: #374151; }
      .header-bottom { display: grid; grid-template-columns: repeat(4, 1fr); border-top: 1px solid #1f2937; }
      .header-cell { padding: 8px 10px; border-right: 1px solid #d1d5db; }
      .header-cell:last-child { border-right: 0; }
      .header-label { font-size: 10px; text-transform: uppercase; color: #6b7280; margin-bottom: 2px; }
      .header-value { font-size: 12px; font-weight: 700; }
      .section { border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px; }
      .section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; margin-bottom: 8px; color: #1f2937; letter-spacing: 0.04em; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; }
      .full { grid-column: 1 / -1; }
      .label { font-size: 10px; text-transform: uppercase; color: #6b7280; margin-bottom: 2px; }
      .value { min-height: 18px; font-size: 12px; }
      .box { min-height: 72px; border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px; background: #ffffff; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #cbd5e1; padding: 7px 6px; text-align: left; vertical-align: top; }
      th { background: #eef2f7; font-size: 11px; }
      .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 14px; }
      .line { border-top: 1px solid #111827; padding-top: 6px; text-align: center; min-height: 42px; font-size: 11px; }
      .foot-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      .note { padding: 8px 10px; border: 1px dashed #94a3b8; border-radius: 6px; background: #f8fafc; color: #475569; font-size: 11px; }
      @media print { .no-print { display: none; } }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="header">
        <div class="header-top">
          <div class="brand">
            <div class="brand-kicker">Sistema de Gestion APR</div>
            <div class="brand-title">Orden de Trabajo</div>
            <div class="brand-subtitle">Resumen operativo para ejecucion en terreno</div>
          </div>
          <div class="docbox">
            <div class="docbox-title">Identificacion del documento</div>
            <div class="docbox-number">${escapeHtml(operation.displayCode)}</div>
            <div class="docbox-meta">
              <div>Emision: ${escapeHtml(formatPrintDate(new Date().toISOString()))}</div>
              <div>Estado OT: ${escapeHtml(statusLabel(operation.status))}</div>
            </div>
          </div>
        </div>
        <div class="header-bottom">
          <div class="header-cell">
            <div class="header-label">Contrato</div>
            <div class="header-value">${escapeHtml(operation.contractNumber || "Sin contrato")}</div>
          </div>
          <div class="header-cell">
            <div class="header-label">Tipo de trabajo</div>
            <div class="header-value">${escapeHtml(operation.operationTypeName || "Operacion")}</div>
          </div>
          <div class="header-cell">
            <div class="header-label">Prioridad</div>
            <div class="header-value">${escapeHtml(mapPriorityLabel(operation.priority))}</div>
          </div>
          <div class="header-cell">
            <div class="header-label">Programada</div>
            <div class="header-value">${escapeHtml(formatPrintDate(operation.scheduledAt))}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Datos de la atencion</div>
        <div class="grid">
          <div>
            <div class="label">Cliente</div>
            <div class="value">${escapeHtml(operation.customerName || "Sin cliente")}</div>
          </div>
          <div>
            <div class="label">Seguimiento</div>
            <div class="value">${escapeHtml(operation.contractNumber || "Sin contrato")}</div>
          </div>
          <div class="full">
            <div class="label">Direccion / referencia</div>
            <div class="value">${escapeHtml(operation.contractAddress || "Sin direccion")}</div>
          </div>
          <div>
            <div class="label">Solicitada</div>
            <div class="value">${escapeHtml(formatPrintDate(operation.requestedAt))}</div>
          </div>
          <div>
            <div class="label">Programada</div>
            <div class="value">${escapeHtml(formatPrintDate(operation.scheduledAt))}</div>
          </div>
          <div class="full">
            <div class="label">Resumen de atencion</div>
            <div class="box">${escapeHtml(operation.requestSummary || "Sin descripcion")}</div>
          </div>
          <div class="full">
            <div class="label">Diagnostico / notas previas</div>
            <div class="box">${escapeHtml(operation.diagnosis || "Sin diagnostico previo")}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Datos de la OT</div>
        <div class="grid">
          <div>
            <div class="label">Estado</div>
            <div class="value">${escapeHtml(statusLabel(operation.status))}</div>
          </div>
          <div>
            <div class="label">Inicio registrado</div>
            <div class="value">${escapeHtml(formatPrintDate(operation.startedAt))}</div>
          </div>
          <div>
            <div class="label">Trabajo a ejecutar</div>
            <div class="value">${escapeHtml(operation.operationTypeName || "Operacion")}</div>
          </div>
          <div>
            <div class="label">Prioridad operativa</div>
            <div class="value">${escapeHtml(mapPriorityLabel(operation.priority))}</div>
          </div>
          <div class="full">
            <div class="label">Operador / cuadrilla asignada</div>
            <div class="box">${escapeHtml(assignedWorkers.join("\n") || "Sin personal asignado")}</div>
          </div>
          <div class="full">
            <div class="label">Vehiculo asignado</div>
            <div class="box">${escapeHtml(assignedVehicles.join("\n") || "Sin vehiculo asignado")}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Materiales utilizados en terreno</div>
        <div class="note">Registrar manualmente los materiales efectivamente utilizados durante la ejecucion del trabajo.</div>
        <table>
          <thead>
            <tr>
              <th style="width: 48px;">#</th>
              <th>Material</th>
              <th style="width: 120px;">Cantidad</th>
              <th style="width: 180px;">Observaciones</th>
            </tr>
          </thead>
          <tbody>${materialRows}</tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Cierre en terreno</div>
        <div class="foot-grid">
          <div>
            <div class="label">Fecha real de ejecucion</div>
            <div class="box"></div>
          </div>
          <div>
            <div class="label">Hora real de ejecucion</div>
            <div class="box"></div>
          </div>
        </div>
        <div class="signatures">
          <div class="line">Firma del operador responsable</div>
          <div class="line">Nombre y firma de recepcion conforme</div>
        </div>
      </div>
    </div>
    <script>
      window.onload = () => {
        window.focus();
        window.print();
      };
    </script>
  </body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }

  async function handleCreateOperation(): Promise<void> {
    if (!session) return;
    if (!selectedCustomer || !newForm.contractId || !newForm.operationTypeId || !newForm.requestSummary.trim()) {
      setError("Completa cliente, contrato, tipo y descripcion para crear la OT.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const created = await createOperation(session, {
        contractId: newForm.contractId,
        operationTypeId: newForm.operationTypeId,
        priority: newForm.priority,
        scheduledAt: newForm.scheduledAt ? `${newForm.scheduledAt}T12:00:00.000Z` : undefined,
        requestSummary: newForm.requestSummary.trim(),
      });
      setOperations((previous) => [created, ...previous]);
      setShowNuevaOT(false);
      setNewForm(emptyForm);
      setSelectedCustomer(null);
      setCustomerContracts([]);
      setCustomerSearchTerm("");
      setCustomerSearchResults([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear la operacion");
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusAction(): Promise<void> {
    if (!session || !selectedOperation) return;

    setSaving(true);
    setError("");
    try {
      let updated: OperationItem;

      if (selectedOperation.status === "requested") {
        updated = await reviewOperation(session, selectedOperation.id, {
          diagnosis: actionNotes || undefined,
          notes: actionNotes || undefined,
        });
      } else if (selectedOperation.status === "reviewed" || selectedOperation.status === "assigned") {
        const printWindow = openOperationPrintWindow();
        updated = await startOperation(session, selectedOperation.id, {
          notes: actionNotes || undefined,
        });
        openOperationPrintSheet(updated, printWindow);
      } else if (selectedOperation.status === "in_progress") {
        const resolution = actionResolution.trim() || actionNotes.trim();
        if (!resolution) {
          setError("Para completar la OT debes indicar una resolucion.");
          setSaving(false);
          return;
        }
        updated = await completeOperation(session, selectedOperation.id, {
          resolution,
          notes: actionNotes || undefined,
        });
      } else {
        setSaving(false);
        return;
      }

      replaceOperationInState(updated);
      setActionNotes("");
      setActionResolution("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo actualizar la operacion");
    } finally {
      setSaving(false);
    }
  }

  async function handleAssignWorker(): Promise<void> {
    if (!session || !selectedOperation) return;
    if (!workerOperarioId) {
      setError("Selecciona un operario.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const rawOp = operations.find((o) => o.id === selectedOperation.id);
      const currentAssignments = (rawOp?.assignments ?? []).map((a) => ({
        operarioId: a.operarioId ?? undefined,
        userId: a.userId ?? undefined,
        roleInOperation: a.roleInOperation,
      }));

      const updated = await assignOperationWorkers(session, selectedOperation.id, {
        assignments: [...currentAssignments, { operarioId: workerOperarioId, roleInOperation: workerRole }],
      });
      replaceOperationInState(updated);
      setWorkerOperarioId("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo asignar personal");
    } finally {
      setSaving(false);
    }
  }

  async function handleAssignVehicle(): Promise<void> {
    if (!session || !selectedOperation) return;
    if (!vehicleId) {
      setError("Selecciona un vehiculo.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const updated = await assignOperationVehicle(session, selectedOperation.id, { vehicleId });
      replaceOperationInState(updated);
      setVehicleId("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo asignar vehiculo");
    } finally {
      setSaving(false);
    }
  }

  async function handleIssueMaterial(): Promise<void> {
    if (!session || !selectedOperation) return;
    if (!warehouseId || !inventoryItemId || !materialQty || materialQty <= 0) {
      setError("Completa bodega, producto y cantidad para rebajar material.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await createStockIssueVoucher(session, {
        operationId: selectedOperation.id,
        warehouseId,
        notes: materialNotes || "Salida por operacion",
        items: [{ inventoryItemId, quantity: materialQty }],
      });

      const refreshed = await listOperations(session);
      setOperations(refreshed);
      setInventoryItemId("");
      setMaterialQty(1);
      setMaterialNotes("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo rebajar material");
    } finally {
      setSaving(false);
    }
  }

  function actionLabel(status: string): string {
    if (status === "requested") return "Revisar OT";
    if (status === "reviewed" || status === "assigned") return "Iniciar OT";
    if (status === "in_progress") return "Completar OT";
    return "Sin acciones";
  }

  async function handleDrop(targetColumn: ColumnId, operationId: string): Promise<void> {
    if (!session) return;
    const operation = operations.find((o) => o.id === operationId);
    if (!operation) return;
    const currentColumn = statusToColumn(operation.status);
    if (currentColumn === targetColumn) return;

    const transitions: Record<string, string> = {
      "pendiente→en_proceso": "in_progress",
      "pendiente→completada": "completed",
      "en_proceso→completada": "completed",
    };
    const key = `${currentColumn}→${targetColumn}`;
    const targetStatus = transitions[key];
    if (!targetStatus) return;

    setSaving(true);
    setError("");
    try {
      let updated: OperationItem;
      if (targetStatus === "in_progress") {
        const printWindow = openOperationPrintWindow();
        updated = await startOperation(session, operationId, {});
        openOperationPrintSheet(updated, printWindow);
      } else {
        const resolution = operation.resolution || "Completado desde kanban";
        updated = await completeOperation(session, operationId, { resolution });
      }
      replaceOperationInState(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cambiar estado");
    } finally {
      setSaving(false);
      setDragOverColumn(null);
    }
  }

  async function handleArchive(operationId: string): Promise<void> {
    if (!session || !window.confirm("¿Archivar esta OT? Quedará en el historial.")) return;
    setSaving(true);
    setError("");
    try {
      await archiveOperation(session, operationId);
      setOperations((prev) => prev.filter((o) => o.id !== operationId));
      setSelectedOperationId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo archivar");
    } finally {
      setSaving(false);
    }
  }

  async function handleUnarchive(operationId: string): Promise<void> {
    if (!session) return;
    setSaving(true);
    setError("");
    try {
      const restored = await unarchiveOperation(session, operationId);
      setArchivedOperations((prev) => prev.filter((o) => o.id !== operationId));
      setOperations((prev) => [restored, ...prev]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo restaurar");
    } finally {
      setSaving(false);
    }
  }

  async function loadHistorial(): Promise<void> {
    if (!session) return;
    setLoadingArchived(true);
    try {
      const rows = await listOperations(session, true);
      setArchivedOperations(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cargar historial");
    } finally {
      setLoadingArchived(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Operaciones</h1>
          <p className="text-sm text-gray-500 mt-0.5">Atenciones, ordenes de trabajo y consumo de materiales</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {[{ id: "kanban", icon: "ri-layout-column-line" }, { id: "lista", icon: "ri-list-check" }, { id: "historial", icon: "ri-archive-line" }].map((tab) => (
              <button
                key={tab.id}
                title={tab.id === "historial" ? "Historial archivadas" : tab.id}
                onClick={() => {
                  setActiveTab(tab.id as ViewMode);
                  if (tab.id === "historial") void loadHistorial();
                }}
                className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                  activeTab === tab.id ? "bg-white text-blue-600" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={`${tab.icon} text-sm`} />
              </button>
            ))}
          </div>
          <button
            onClick={openNewOperationModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Nueva OT
          </button>
        </div>
      </div>

      {error ? <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div> : null}

      <div className="grid grid-cols-3 gap-3">
        <StatCard color="bg-amber-500" label="Pendiente" value={stats.pendiente} />
        <StatCard color="bg-blue-500" label="En Proceso" value={stats.enProceso} />
        <StatCard color="bg-emerald-500" label="Completada" value={stats.completada} />
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-gray-500">Cargando operaciones...</div>
      ) : activeTab === "kanban" ? (
        <div className="grid grid-cols-3 gap-4">
          {columnConfig.map((column) => {
            const items = uiOperations.filter((operation) => operation.column === column.id);
            return (
              <div
                key={column.id}
                className={`bg-gray-50 rounded-xl border-2 overflow-hidden transition-colors ${dragOverColumn === column.id ? "border-blue-400 bg-blue-50" : "border-gray-200"}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverColumn(column.id); }}
                onDragLeave={() => setDragOverColumn(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("operationId");
                  if (id) void handleDrop(column.id, id);
                  setDragOverColumn(null);
                }}
              >
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-white">
                  <div className={`w-2.5 h-2.5 rounded-full ${column.color}`} />
                  <span className="text-sm font-semibold text-gray-700">{column.label}</span>
                  <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{items.length}</span>
                </div>
                <div className="p-3 space-y-3 min-h-48">
                  {items.map((operation) => {
                    const category = categoryConfig[operation.operationCategory] ?? categoryConfig.reparacion;
                    return (
                      <div
                        key={operation.id}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData("operationId", operation.id); e.dataTransfer.effectAllowed = "move"; }}
                        onClick={() => setSelectedOperationId(operation.id)}
                        className="bg-white rounded-xl border border-gray-100 p-3 cursor-grab active:cursor-grabbing hover:border-blue-200 hover:-translate-y-0.5 transition-all duration-150"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-400 font-mono">{operation.displayCode}</span>
                          <Badge variant={operation.priorityVariant} size="sm">{operation.priorityLabel}</Badge>
                        </div>
                        <div className="flex items-start gap-2 mb-2">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${category.bg}`}>
                            <i className={`${category.icon} text-xs ${category.color}`} />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-gray-700 leading-snug">{operation.operationLabel}</div>
                            <div className="text-xs text-gray-500 leading-snug mt-0.5">{operation.customerLabel}</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <i className="ri-calendar-line text-xs" />
                            {operation.scheduledAt || operation.requestedAt}
                          </div>
                          <div className="text-[11px] text-gray-500 max-w-[45%] truncate">{operation.technicianLabel}</div>
                        </div>
                      </div>
                    );
                  })}
                  {items.length === 0 ? <div className="text-center py-6 text-xs text-gray-400">Sin ordenes</div> : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : activeTab === "lista" ? (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">OT</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Programacion</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Prioridad</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
              </tr>
            </thead>
            <tbody>
              {uiOperations.map((operation, index) => {
                const category = categoryConfig[operation.operationCategory] ?? categoryConfig.reparacion;
                return (
                  <tr
                    key={operation.id}
                    onClick={() => setSelectedOperationId(operation.id)}
                    className={`border-b border-gray-50 hover:bg-blue-50/20 cursor-pointer transition-colors ${index % 2 === 0 ? "" : "bg-gray-50/30"}`}
                  >
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{operation.displayCode}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 flex items-center justify-center rounded-md ${category.bg}`}>
                          <i className={`${category.icon} text-xs ${category.color}`} />
                        </div>
                        <span className="text-sm text-gray-700">{operation.operationLabel}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{operation.customerLabel}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{operation.scheduledAt || operation.requestedAt}</td>
                    <td className="px-4 py-3 text-center"><Badge variant={operation.priorityVariant} size="sm">{operation.priorityLabel}</Badge></td>
                    <td className="px-4 py-3 text-center"><Badge variant={statusBadgeVariant(operation.status)} size="sm">{statusLabel(operation.status)}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : activeTab === "historial" ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                value={historialSearch}
                onChange={(e) => setHistorialSearch(e.target.value)}
                placeholder="Buscar por cliente, tipo o OT..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              />
            </div>
            <span className="text-xs text-gray-500">{archivedOperations.length} OT archivadas</span>
          </div>
          {loadingArchived ? (
            <div className="h-16 bg-gray-100 rounded-xl animate-pulse" />
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">OT</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Completada</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Prioridad</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedOperations
                    .filter((op) => {
                      if (!historialSearch.trim()) return true;
                      const q = historialSearch.toLowerCase();
                      return (
                        op.id.toLowerCase().includes(q) ||
                        formatOperationCode(op.sequenceNumber).toLowerCase().includes(q) ||
                        op.customerName.toLowerCase().includes(q) ||
                        op.operationTypeName.toLowerCase().includes(q) ||
                        op.contractNumber.toLowerCase().includes(q)
                      );
                    })
                    .map((op, index) => {
                      const category = categoryConfig[normalizeCategory(op.operationTypeCategory)] ?? categoryConfig.reparacion;
                      return (
                        <tr key={op.id} className={`border-b border-gray-50 ${index % 2 === 0 ? "" : "bg-gray-50/30"}`}>
                          <td className="px-4 py-3 text-xs font-mono text-gray-500">{formatOperationCode(op.sequenceNumber)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0 ${category.bg}`}>
                                <i className={`${category.icon} text-xs ${category.color}`} />
                              </div>
                              <span className="text-sm text-gray-700">{op.operationTypeName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{op.customerName}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{op.completedAt ? new Date(op.completedAt).toLocaleDateString("es-CL") : "—"}</td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant={mapPriorityVariant(op.priority)} size="sm">{mapPriorityLabel(op.priority)}</Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              disabled={saving}
                              onClick={() => void handleUnarchive(op.id)}
                              className="text-xs px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-60"
                            >
                              <i className="ri-archive-drawer-line mr-1" />
                              Restaurar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  {archivedOperations.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Sin OT archivadas</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {selectedOperation ? (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedOperationId(null)} />
          <div className="relative bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div>
                <h2 className="font-semibold text-gray-800">{selectedOperation.displayCode}</h2>
                <p className="text-xs text-gray-400">{selectedOperation.operationLabel}</p>
              </div>
              <button onClick={() => setSelectedOperationId(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <Badge variant={selectedOperation.priorityVariant}>Prioridad {selectedOperation.priorityLabel}</Badge>
                <Badge variant={statusBadgeVariant(selectedOperation.status)}>{statusLabel(selectedOperation.status)}</Badge>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Resumen</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{selectedOperation.summary}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Cliente</h3>
                  <p className="text-gray-700">{selectedOperation.customerLabel}</p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Seguimiento</h3>
                  <p className="text-gray-700">{selectedOperation.contractLabel}</p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Solicitada</h3>
                  <p className="text-gray-700">{selectedOperation.requestedAt}</p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Programada</h3>
                  <p className="text-gray-700">{selectedOperation.scheduledAt || "Sin fecha"}</p>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Atencion y avance</h3>
                <textarea
                  value={actionNotes}
                  onChange={(event) => setActionNotes(event.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none"
                  rows={2}
                  maxLength={500}
                  placeholder="Notas de atencion..."
                />
                {selectedOperation.status === "in_progress" ? (
                  <textarea
                    value={actionResolution}
                    onChange={(event) => setActionResolution(event.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none"
                    rows={2}
                    maxLength={500}
                    placeholder="Resolucion final..."
                  />
                ) : null}
                {selectedOperation.status !== "completed" ? (
                  <button
                    disabled={saving}
                    onClick={() => void handleStatusAction()}
                    className="w-full py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {saving ? "Procesando..." : actionLabel(selectedOperation.status)}
                  </button>
                ) : (
                  <button
                    disabled={saving}
                    onClick={() => void handleArchive(selectedOperation.id)}
                    className="w-full py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer disabled:opacity-60"
                  >
                    <i className="ri-archive-line mr-1.5" />
                    Archivar OT
                  </button>
                )}
              </div>

              <div className="border-t pt-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Asignación de personal</h3>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={workerOperarioId}
                    onChange={(event) => setWorkerOperarioId(event.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  >
                    <option value="">Selecciona operario</option>
                    {operarios.filter((o) => o.active).map((o) => (
                      <option key={o.id} value={o.id}>{o.name} · {o.level === "jefe" ? "Jefe" : o.level === "senior" ? "Senior" : "Técnico"}</option>
                    ))}
                  </select>
                  <select
                    value={workerRole}
                    onChange={(event) => setWorkerRole(event.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  >
                    <option value="tecnico">Técnico</option>
                    <option value="jefe_cuadrilla">Jefe cuadrilla</option>
                    <option value="apoyo">Apoyo</option>
                  </select>
                </div>
                <button
                  disabled={saving}
                  onClick={() => void handleAssignWorker()}
                  className="w-full py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                >
                  Agregar operario
                </button>
                {(() => {
                  const rawOp = operations.find((o) => o.id === selectedOperation.id);
                  const assigned = (rawOp?.assignments ?? []);
                  if (assigned.length === 0) return <div className="text-xs text-gray-400">Sin personal asignado</div>;
                  return (
                    <ul className="text-xs text-gray-600 space-y-1">
                      {assigned.map((a) => {
                        const op = operarios.find((x) => x.id === a.operarioId);
                        return (
                          <li key={a.id}>{op ? `${op.name} · ${a.roleInOperation}` : (a.userId ?? a.operarioId ?? "—")}</li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </div>

              <div className="border-t pt-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Asignacion de vehiculo</h3>
                <select
                  value={vehicleId}
                  onChange={(event) => setVehicleId(event.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                >
                  <option value="">Selecciona vehiculo</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>{vehicle.code} · {vehicle.name} · {vehicle.plate}</option>
                  ))}
                </select>
                <button
                  disabled={saving}
                  onClick={() => void handleAssignVehicle()}
                  className="w-full py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                >
                  Asignar vehiculo
                </button>
                {selectedOperation.vehicles.length > 0 ? (
                  <ul className="text-xs text-gray-600 space-y-1">
                    {selectedOperation.vehicles.map((vehicle) => (
                      <li key={vehicle.id}>{vehicle.code || "VEH"} · {vehicle.name} · {vehicle.plate}</li>
                    ))}
                  </ul>
                ) : <div className="text-xs text-gray-400">Sin vehiculos asignados</div>}
              </div>

              <div className="border-t pt-4 space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Materiales y rebaja de stock</h3>
                <select
                  value={warehouseId}
                  onChange={(event) => setWarehouseId(event.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                >
                  <option value="">Selecciona bodega</option>
                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>{warehouse.code} · {warehouse.name}</option>
                  ))}
                </select>
                <select
                  value={inventoryItemId}
                  onChange={(event) => setInventoryItemId(event.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                >
                  <option value="">Selecciona producto</option>
                  {inventoryItems.map((item) => (
                    <option key={item.id} value={item.id}>{item.sku} · {item.name}</option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    min={1}
                    value={materialQty}
                    onChange={(event) => setMaterialQty(Number(event.target.value || 1))}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                    placeholder="Cantidad"
                  />
                  <input
                    value={materialNotes}
                    onChange={(event) => setMaterialNotes(event.target.value)}
                    className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                    placeholder="Nota"
                  />
                </div>
                <button
                  disabled={saving}
                  onClick={() => void handleIssueMaterial()}
                  className="w-full py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100"
                >
                  Rebajar material en inventario
                </button>

                {selectedOperation.materials.length > 0 ? (
                  <ul className="text-xs text-gray-600 space-y-1">
                    {selectedOperation.materials.map((material) => (
                      <li key={material.id}>{material.materialName} · usado: {material.quantityUsed || material.quantityRequested}</li>
                    ))}
                  </ul>
                ) : <div className="text-xs text-gray-400">Sin materiales registrados</div>}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showNuevaOT ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowNuevaOT(false)} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">Nueva Orden de Trabajo</h2>
              <button onClick={() => setShowNuevaOT(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Buscar cliente</label>
                  <input
                    value={customerSearchTerm}
                    onChange={(event) => setCustomerSearchTerm(event.target.value)}
                    disabled={Boolean(selectedCustomer)}
                    placeholder="Nombre o RUT (min. 2 caracteres)"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  {selectedCustomer ? (
                    <div className="mt-2 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <div className="text-sm text-emerald-800">
                        <div className="font-medium">{selectedCustomer.nombre}</div>
                        <div className="text-xs">{selectedCustomer.rut}</div>
                      </div>
                      <button
                        type="button"
                        onClick={handleResetSelectedCustomer}
                        className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-white border border-emerald-200 rounded-md hover:bg-emerald-100"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : null}
                  {!selectedCustomer ? (
                    <p className="mt-1 text-[11px] text-gray-400">
                      {searchingCustomers
                        ? "Buscando clientes..."
                        : customerSearchTerm.trim().length < 2
                          ? "Escribe al menos 2 caracteres."
                          : `${customerSearchResults.length} cliente(s) encontrado(s)`}
                    </p>
                  ) : null}
                  {!selectedCustomer && customerSearchResults.length > 0 ? (
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white">
                      {customerSearchResults.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => void handleSelectCustomer(option)}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50"
                        >
                          <div className="text-sm font-medium text-gray-800">{option.nombre}</div>
                          <div className="text-xs text-gray-500">{option.rut}</div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Contrato</label>
                  <select
                    value={newForm.contractId}
                    disabled={!selectedCustomer || loadingCustomerContracts}
                    onChange={(event) => setNewForm((previous) => ({ ...previous, contractId: event.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 cursor-pointer disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">
                      {!selectedCustomer
                        ? "Selecciona cliente primero"
                        : loadingCustomerContracts
                          ? "Cargando contratos..."
                          : "Selecciona contrato"}
                    </option>
                    {customerContracts.map((contract) => (
                      <option key={contract.id} value={contract.id}>
                        {contract.contractNumber} · {contract.startAddress}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tipo de trabajo</label>
                  <select value={newForm.operationTypeId} onChange={(event) => setNewForm((previous) => ({ ...previous, operationTypeId: event.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 cursor-pointer">
                    <option value="">Selecciona tipo</option>
                    {operationTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Prioridad</label>
                  <select value={newForm.priority} onChange={(event) => setNewForm((previous) => ({ ...previous, priority: event.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 cursor-pointer">
                    <option value="urgent">Alta</option>
                    <option value="normal">Media</option>
                    <option value="low">Baja</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fecha programada</label>
                  <input type="date" value={newForm.scheduledAt} onChange={(event) => setNewForm((previous) => ({ ...previous, scheduledAt: event.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 cursor-pointer" />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Descripcion</label>
                  <textarea value={newForm.requestSummary} onChange={(event) => setNewForm((previous) => ({ ...previous, requestSummary: event.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none" rows={3} maxLength={500} placeholder="Descripcion del trabajo a realizar..." />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button onClick={() => setShowNuevaOT(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
                <button disabled={saving} onClick={() => void handleCreateOperation()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">{saving ? "Creando..." : "Crear OT"}</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function normalizeCategory(value: string): string {
  const normalized = value.toLowerCase();
  if (normalized.includes("instal")) return "instalacion";
  if (normalized.includes("lectur")) return "lectura";
  if (normalized.includes("cort")) return "corte";
  return "reparacion";
}

function statusToColumn(status: string): ColumnId {
  if (status === "completed") return "completada";
  if (status === "in_progress") return "en_proceso";
  return "pendiente";
}

function mapPriorityLabel(priority: string): "alta" | "media" | "baja" {
  const normalized = priority.toLowerCase();
  if (normalized === "urgent" || normalized === "high" || normalized === "alta") return "alta";
  if (normalized === "low" || normalized === "baja") return "baja";
  return "media";
}

function mapPriorityVariant(priority: string): "error" | "warning" | "success" {
  const label = mapPriorityLabel(priority);
  if (label === "alta") return "error";
  if (label === "baja") return "success";
  return "warning";
}

function statusBadgeVariant(status: string): "success" | "warning" | "neutral" | "info" {
  if (status === "completed") return "success";
  if (status === "in_progress") return "info";
  if (status === "requested" || status === "reviewed" || status === "assigned") return "warning";
  return "neutral";
}

function formatOperationCode(sequenceNumber: number): string {
  const safeNumber = Number.isFinite(sequenceNumber) && sequenceNumber > 0 ? Math.trunc(sequenceNumber) : 0;
  return `OT-${String(safeNumber).padStart(6, "0")}`;
}

function statusLabel(status: string): string {
  if (status === "requested") return "Solicitada";
  if (status === "reviewed") return "Revisada";
  if (status === "assigned") return "Asignada";
  if (status === "in_progress") return "En proceso";
  if (status === "completed") return "Completada";
  return status;
}

function toDateLabel(value: string): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("es-CL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatPrintDate(value: string): string {
  if (!value) return "Sin registro";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Sin registro";
  return parsed.toLocaleString("es-CL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\n/g, "<br />");
}

function StatCard({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${color}`} />
      <div>
        <div className="text-xl font-bold text-gray-800">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}


